import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { FileText, Package, CheckCircle, Clock, Truck, Paperclip, ClipboardCheck, ShieldCheck, ClipboardList, Warehouse, X, History } from 'lucide-react';

const ORDER_HISTORY_ACTION_TITLES = {
  created: 'Order Created',
  updated: 'Order Updated',
  submitted_for_approval: 'Submitted For Approval',
  approved: 'Order Approved',
  rejected: 'Order Rejected',
  rejected_by_ops: 'Rejected By Ops',
  put_on_hold: 'Put On Hold',
  schedule_set: 'Dispatch Schedule Set',
  reset: 'Schedule Reset',
  finance_updated: 'Finance Details Updated',
  finance_approved: 'Finance Approved',
  finance_rejected: 'Finance Rejected',
  finance_hold: 'Finance Hold',
  delivery_confirmed: 'Delivery Confirmed'
};

const ORDER_TIMELINE_STAGES = [
  { id: 'order_created', label: 'Order Created', icon: <FileText size={18} /> },
  { id: 'order_updated', label: 'Order Updated', icon: <FileText size={18} /> },
  { id: 'submitted_for_approval', label: 'Submitted For Approval', icon: <Clock size={18} /> },
  { id: 'sales_manager_review', label: 'Sales Manager Review', icon: <ShieldCheck size={18} /> },
  { id: 'sent_to_dispatch_queue', label: 'Sent To Dispatch Queue', icon: <Truck size={18} /> },
  { id: 'dispatch_scheduled', label: 'Dispatch Scheduled', icon: <Clock size={18} /> }
];

const TIMELINE_STAGES = [
  { id: 'order_created', label: 'Order Created', icon: <FileText size={18} /> },
  { id: 'moc_created', label: 'MOC Created', icon: <Package size={18} /> },
  { id: 'moc_approved_finance', label: 'MOC Approved', icon: <ShieldCheck size={18} /> },
  { id: 'packing_scheduled', label: 'Packing Scheduled', icon: <Clock size={18} /> },
  { id: 'packed_ready', label: 'Packing Done', icon: <ClipboardCheck size={18} /> },
  { id: 'dc_created', label: 'DC Completed', icon: <FileText size={18} /> },
  { id: 'documents_ready', label: 'Documents Ready', icon: <Paperclip size={18} /> },
  { id: 'qc_dispatched', label: 'QC & Dispatched', icon: <Truck size={18} /> },
  { id: 'delivered', label: 'Delivered', icon: <CheckCircle size={18} /> },
];

const INTERBRANCH_TIMELINE_STAGES = [
  { id: 'moc_created', label: 'MOC Created', icon: <Package size={18} /> },
  { id: 'packing_scheduled', label: 'Packing Scheduled', icon: <Clock size={18} /> },
  { id: 'packed_ready', label: 'Packing Done', icon: <ClipboardCheck size={18} /> },
  { id: 'dc_created', label: 'DC Completed', icon: <FileText size={18} /> },
  { id: 'documents_ready', label: 'Documents Ready', icon: <Paperclip size={18} /> },
  { id: 'qc_dispatched', label: 'QC & Dispatched', icon: <Truck size={18} /> },
  { id: 'delivered', label: 'Delivered', icon: <CheckCircle size={18} /> },
];

const DISPATCH_LOG_STAGES = [
  { id: 'moc_created', label: 'MOC Created', icon: <Package size={18} /> },
  { id: 'moc_ops_review', label: 'MOC Ops head approved/rejected', icon: <ShieldCheck size={18} /> },
  { id: 'moc_finance_review', label: 'MOC Finance approved/rejected', icon: <ShieldCheck size={18} /> },
  { id: 'packing_scheduled', label: 'Packing Scheduled', icon: <Clock size={18} /> },
  { id: 'packed_ready', label: 'Packing Ready', icon: <ClipboardCheck size={18} /> },
  { id: 'dc_created', label: 'Delivery Challan Ready', icon: <FileText size={18} /> },
  { id: 'documents_ready', label: 'Invoice Ready', icon: <Paperclip size={18} /> },
  { id: 'qc_dispatched', label: 'QC Complete', icon: <Truck size={18} /> },
  { id: 'dispatched', label: 'Dispatched', icon: <CheckCircle size={18} /> },
  { id: 'delivered', label: 'Delivered', icon: <CheckCircle size={18} /> },
];

const getTimelineStageFromStatus = (status, isInterbranchTransfer = false) => {
  switch (status) {
    case 'draft':
    case 'confirmed_order':
    case 'in_que':
    case 'ops_rejected':
    case 'ops_hold':
      return 'order_created';
    case 'moc_draft':
    case 'moc_in_progress':
      return 'moc_created';
    case 'moc_pending_finance':
    case 'fin_hold':
    case 'fin_rejected':
      return 'moc_approved_finance';
    case 'moc_rejected':
      return 'moc_created';
    case 'moc_approved':
    case 'packing_queued':
      return isInterbranchTransfer ? 'moc_created' : 'moc_approved_finance';
    case 'dc_pending':
    case 'packing_scheduled':
    case 'packing_hold':
    case 'packing_rejected':
      return 'packing_scheduled';
    case 'packing': 
    case 'packed_ready':
      return 'packed_ready';
    case 'invoice_pending':
    case 'ewaybill_pending':
    case 'dc_hold':
      return 'dc_created';
    case 'invoice_ready':
    case 'ewaybill_complete':
    case 'dc_confirmed':
      return 'documents_ready';
    case 'qc_passed':
    case 'dispatched':
      return 'qc_dispatched';
    case 'in_transit':
    case 'delivered':
      return 'delivered';
    default:
      return isInterbranchTransfer ? 'moc_created' : 'order_created';
  }
};

const getDispatchLogStageFromData = (dispatchData = {}) => {
  const dispatchStatus = String(dispatchData?.dispatchStatus || '').trim();
  const packingStatus = String(dispatchData?.packingStatus || '').trim();

  if (['moc_draft', 'moc_in_progress'].includes(dispatchStatus)) {
    return 'moc_created';
  }

  if (['invoice_pending'].includes(dispatchStatus)) {
    return 'dc_created';
  }

  if (['invoice_ready'].includes(dispatchStatus)) {
    return 'documents_ready';
  }

  if (['delivered'].includes(dispatchStatus)) {
    return 'delivered';
  }

  if (['dispatched'].includes(dispatchStatus)) {
    return 'dispatched';
  }

  if (['packed_ready'].includes(packingStatus) || ['packed_ready'].includes(dispatchStatus)) {
    return 'packed_ready';
  }

  if (
    ['packing_scheduled'].includes(packingStatus) ||
    ['packing_scheduled', 'dc_pending'].includes(dispatchStatus)
  ) {
    return 'packing_scheduled';
  }

  if (['moc_approved', 'fin_hold', 'fin_rejected'].includes(dispatchStatus)) {
    return 'moc_finance_review';
  }

  if (
    dispatchData.opsApprovedAt ||
    dispatchData.opsRejectedAt ||
    dispatchData.opsReviewedAt ||
    ['moc_pending_approval', 'moc_pending_finance', 'moc_rejected', 'moc_submitted'].includes(dispatchStatus)
  ) {
    return 'moc_ops_review';
  }

  return 'moc_created';
};

const formatHistoryDateTime = (value) => {
  if (!value) return '---';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatHistoryAction = (action = '') =>
  ORDER_HISTORY_ACTION_TITLES[action] ||
  String(action || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatStatusLabel = (status = '') => {
  const normalized = String(status || '').trim();
  if (!normalized) return '---';
  return normalized
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatTimelineTimestamp = (timestamp) => {
  if (!timestamp) return '';
  let date;
  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (timestamp?.toDate) {
    date = timestamp.toDate();
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else {
    return '';
  }

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatTimelineDateTime = (timestamp) => {
  if (!timestamp) return '';
  let date;
  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (timestamp?.toDate) {
    date = timestamp.toDate();
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else {
    return '';
  }

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatDispatchOutTimestamp = (vehicleOutTime, dispatchDate) => {
  const timePart = String(vehicleOutTime || '').trim();
  const datePart = String(dispatchDate || '').trim();

  if (!timePart && !datePart) return '';
  if (!datePart) return timePart;
  if (!timePart) return formatTimelineDateTime(datePart);

  const parsedDate = new Date(datePart);
  if (Number.isNaN(parsedDate.getTime())) {
    return `${datePart} ${timePart}`.trim();
  }

  const formattedDate = parsedDate.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  return `${formattedDate} ${timePart}`;
};

const findLatestHistoryEntry = (history = [], matcher) => {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (matcher(history[index])) return history[index];
  }
  return null;
};

const getOrderTimelineState = (orderData = {}, historyData = {}) => {
  const history = Array.isArray(historyData?.history) ? historyData.history : [];
  const currentOrderStatus = String(orderData?.status || historyData?.orderStatus || '').trim();
  const isAwaitingSalesApproval = currentOrderStatus === 'pending_approval';
  const latestUpdatedEntry = findLatestHistoryEntry(history, (entry) => entry?.action === 'updated');
  const submittedEntry = findLatestHistoryEntry(history, (entry) => entry?.action === 'submitted_for_approval');
  const decisionEntry = findLatestHistoryEntry(history, (entry) => ['approved', 'rejected'].includes(entry?.action));
  const dispatchQueueEntry = findLatestHistoryEntry(
    history,
    (entry) => entry?.orderStatus === 'in_que' || entry?.action === 'approved'
  );
  const scheduleEntry = findLatestHistoryEntry(history, (entry) => entry?.action === 'schedule_set');
  const latestSalesReview = Array.isArray(orderData?.salesReviewHistory) && orderData.salesReviewHistory.length > 0
    ? orderData.salesReviewHistory[orderData.salesReviewHistory.length - 1]
    : null;

  const decisionLabel =
    isAwaitingSalesApproval
      ? 'Submitted For Approval'
      : (decisionEntry?.action === 'rejected' || latestSalesReview?.status === 'rejected'
      ? 'Sales Manager Rejected'
      : decisionEntry?.action === 'approved' || latestSalesReview?.status === 'in_que'
        ? 'Sales Manager Approved'
        : 'Sales Manager Review');
  const isSalesManagerRejected = decisionLabel === 'Sales Manager Rejected';

  const timestamps = {
    order_created: historyData?.createdAt || orderData?.createdAt || '',
    order_updated: latestUpdatedEntry?.actionAt || latestUpdatedEntry?.updatedAt || orderData?.updatedAt || '',
    submitted_for_approval: submittedEntry?.actionAt || submittedEntry?.updatedAt || '',
    sales_manager_review:
      decisionEntry?.actionAt ||
      decisionEntry?.updatedAt ||
      latestSalesReview?.reviewedAt ||
      orderData?.salesReviewedAt ||
      '',
    sent_to_dispatch_queue:
      dispatchQueueEntry?.actionAt ||
      dispatchQueueEntry?.updatedAt ||
      (latestSalesReview?.status === 'in_que' ? latestSalesReview?.reviewedAt : '') ||
      '',
    dispatch_scheduled:
      scheduleEntry?.actionAt ||
      historyData?.scheduleSetAt ||
      orderData?.dispatchScheduleUpdatedAt ||
      orderData?.dispatchScheduledAt ||
      ''
  };

  let currentStageIndex = 0;
  if (isAwaitingSalesApproval) currentStageIndex = 2;
  else if (timestamps.dispatch_scheduled) currentStageIndex = 5;
  else if (timestamps.sent_to_dispatch_queue) currentStageIndex = 4;
  else if (timestamps.sales_manager_review) currentStageIndex = 3;
  else if (timestamps.submitted_for_approval) currentStageIndex = 2;
  else if (timestamps.order_updated && timestamps.order_updated !== timestamps.order_created) currentStageIndex = 1;
  else if (timestamps.order_created) currentStageIndex = 0;

  const stages = ORDER_TIMELINE_STAGES.map((stage) =>
    stage.id === 'sales_manager_review'
      ? { ...stage, label: decisionLabel, tone: isSalesManagerRejected ? 'rejected' : 'default' }
      : stage
  );

  return { stages, timestamps, currentStageIndex };
};

const getDispatchTimelineState = (dispatchData = {}) => {
  const currentStageId = getDispatchLogStageFromData(dispatchData);
  const currentStageIndex = Math.max(0, DISPATCH_LOG_STAGES.findIndex((stage) => stage.id === currentStageId));
  const dispatchStatus = String(dispatchData?.dispatchStatus || '').trim();
  const packingStatus = String(dispatchData?.packingStatus || '').trim();
  const isOpsRejected = Boolean(
    dispatchData.opsRejectedAt ||
    dispatchData.dispatchStatus === 'moc_rejected' ||
    dispatchData.opsReviewStatus === 'rejected'
  );
  const isFinanceRejected = Boolean(
    dispatchData.financeRejectedAt ||
    dispatchData.dispatchStatus === 'fin_rejected'
  );
  const isFinanceHeld = Boolean(
    dispatchData.financeHoldAt ||
    dispatchData.dispatchStatus === 'fin_hold'
  );

  const timestamps = {
    moc_created: dispatchData.createdAt || dispatchData.mocCreatedAt || dispatchData.updatedAt || '',
    moc_ops_review:
      dispatchData.opsApprovedAt ||
      dispatchData.opsRejectedAt ||
      dispatchData.opsReviewedAt ||
      dispatchData.updatedAt ||
      '',
    moc_finance_review:
      dispatchData.financeApprovedAt ||
      dispatchData.financeRejectedAt ||
      dispatchData.financeHoldAt ||
      '',
    packing_scheduled:
      dispatchData.packingScheduledAt ||
      dispatchData.updatedAt ||
      '',
    packed_ready:
      dispatchData.packedReadyAt ||
      dispatchData.packedAt ||
      dispatchData.updatedAt ||
      '',
    dc_created:
      dispatchData.dcCreatedAt ||
      dispatchData.dcSubmittedAt ||
      dispatchData.updatedAt ||
      '',
    documents_ready:
      dispatchData.invoiceSubmittedAt ||
      dispatchData.updatedAt ||
      '',
    qc_dispatched:
      dispatchData.qcUpdatedAt ||
      dispatchData.dispatchConfirmedAt ||
      dispatchData.updatedAt ||
      '',
    dispatched:
      dispatchData.dispatchConfirmedAt ||
      dispatchData.dispatchedAt ||
      dispatchData.vehicleOutTime ||
      dispatchData.dispatchDate ||
      dispatchData.updatedAt ||
      '',
    delivered:
      dispatchData.deliveredAt ||
      dispatchData.deliveryConfirmedAt ||
      dispatchData.updatedAt ||
      ''
  };

  const stageStates = {
    moc_created: {
      completed: Boolean(dispatchData.createdAt || dispatchData.mocCreatedAt)
    },
    moc_ops_review: {
      completed: Boolean(
        dispatchData.opsApprovedAt ||
        dispatchData.opsRejectedAt ||
        dispatchData.opsReviewedAt ||
        ['moc_pending_finance', 'moc_approved', 'moc_rejected', 'fin_hold', 'fin_rejected'].includes(dispatchStatus)
      )
    },
    moc_finance_review: {
      completed: Boolean(
        dispatchData.financeApprovedAt ||
        ['moc_approved', 'fin_hold', 'fin_rejected', 'packing_scheduled', 'packed_ready', 'dc_pending', 'invoice_pending', 'invoice_ready', 'dispatched', 'delivered'].includes(dispatchStatus)
      )
    },
    packing_scheduled: {
      completed: Boolean(
        dispatchData.packingScheduledAt ||
        ['packing_scheduled', 'packed_ready', 'dc_pending', 'invoice_pending', 'invoice_ready', 'dispatched', 'delivered'].includes(dispatchStatus) ||
        ['packing_scheduled', 'packed_ready'].includes(packingStatus)
      )
    },
    packed_ready: {
      completed: packingStatus === 'packed_ready'
    },
    dc_created: {
      completed: Boolean(
        dispatchData.dcCreatedAt ||
        dispatchData.dcSubmittedAt ||
        ['invoice_pending', 'invoice_ready', 'dispatched', 'delivered'].includes(dispatchStatus)
      )
    },
    documents_ready: {
      completed: Boolean(
        dispatchData.invoiceSubmittedAt ||
        ['invoice_ready', 'dispatched', 'delivered'].includes(dispatchStatus)
      )
    },
    qc_dispatched: {
      completed: Boolean(
        dispatchData.qcUpdatedAt ||
        dispatchData.dispatchConfirmedAt ||
        ['dispatched', 'delivered'].includes(dispatchStatus)
      )
    },
    dispatched: {
      completed: Boolean(
        dispatchData.dispatchConfirmedAt ||
        ['dispatched', 'delivered'].includes(dispatchStatus)
      )
    },
    delivered: {
      completed: dispatchStatus === 'delivered'
    }
  };

  const stageLabels = {
    moc_ops_review: isOpsRejected ? 'MOC Ops head rejected' : 'MOC Ops head approved',
    moc_finance_review: isFinanceHeld
      ? 'MOC Finance hold'
      : isFinanceRejected
        ? 'MOC Finance rejected'
        : 'MOC Finance approved',
    packing_scheduled: 'Packing Scheduled',
    packed_ready: 'Packing Ready',
    dc_created: 'Delivery Challan Ready',
    documents_ready: 'Invoice Ready',
    qc_dispatched: 'QC Complete',
    dispatched: 'Dispatched',
    delivered: 'Delivered'
  };

  const stages = DISPATCH_LOG_STAGES.map((stage) =>
    stageLabels[stage.id]
      ? {
          ...stage,
          label: stageLabels[stage.id],
          tone:
            (stage.id === 'moc_ops_review' && isOpsRejected) ||
            (stage.id === 'moc_finance_review' && (isFinanceRejected || isFinanceHeld))
              ? 'rejected'
              : 'default'
        }
      : stage
  );

  return { stages, timestamps, currentStageIndex, currentStageId, stageStates };
};

const OrderHistoryDrawer = ({ isOpen, onClose, orderData, historyData }) => {
  const history = useMemo(() => {
    const items = Array.isArray(historyData?.history) ? historyData.history : [];
    return [...items].sort((a, b) => {
      const aTime = new Date(a.actionAt || a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.actionAt || b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [historyData]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        zIndex: 9998
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '440px',
          maxWidth: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-12px 0 28px rgba(15, 23, 42, 0.18)',
          padding: '24px 24px 32px 24px',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#111827' }}>Order History</h3>
            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
              #{orderData?.orderNumber || orderData?.id || historyData?.orderId || '---'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gap: '14px', marginBottom: '20px' }}>
          <div style={historySummaryCardStyle}>
            <div style={historyMetaLabelStyle}>Created</div>
            <div style={historyPrimaryValueStyle}>{formatHistoryDateTime(historyData?.createdAt)}</div>
            <div style={historySecondaryValueStyle}>
              {historyData?.createdBy || '---'} {historyData?.createdByEmail ? `(${historyData.createdByEmail})` : ''}
            </div>
          </div>
          <div style={historySummaryCardStyle}>
            <div style={historyMetaLabelStyle}>Last Updated</div>
            <div style={historyPrimaryValueStyle}>{formatHistoryDateTime(historyData?.updatedAt)}</div>
            <div style={historySecondaryValueStyle}>
              {historyData?.updatedBy || '---'} {historyData?.updatedByEmail ? `(${historyData.updatedByEmail})` : ''}
            </div>
          </div>
          <div style={historySummaryCardStyle}>
            <div style={historyMetaLabelStyle}>Current Status</div>
            <div style={historyPrimaryValueStyle}>{formatStatusLabel(historyData?.orderStatus || orderData?.status)}</div>
            <div style={historySecondaryValueStyle}>Audit entries: {history.length}</div>
          </div>
        </div>

        <div style={{ marginBottom: '12px', fontSize: '12px', fontWeight: '800', color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Activity Log
        </div>

        {history.length === 0 ? (
          <div style={emptyHistoryStyle}>No history recorded yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {history.map((entry, index) => (
              <div key={`${entry.actionAt || entry.updatedAt || entry.createdAt || 'history'}-${index}`} style={historyCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                    {formatHistoryAction(entry.action)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {formatHistoryDateTime(entry.actionAt || entry.updatedAt || entry.createdAt)}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '5px', fontSize: '13px', color: '#334155' }}>
                  <div>
                    <span style={historyMetaLabelInlineStyle}>Status</span>
                    <span style={historyStatusPillStyle}>{formatStatusLabel(entry.orderStatus)}</span>
                  </div>
                  <div>By: {entry.actionBy || entry.updatedBy || '---'}</div>
                  <div>Email: {entry.actionByEmail || entry.updatedByEmail || '---'}</div>
                  {entry.remarks ? <div>Remarks: {entry.remarks}</div> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DispatchHistoryDrawer = ({ isOpen, onClose, db, dispatchData }) => {
  const [auditData, setAuditData] = useState(null);

  useEffect(() => {
    if (!isOpen || !db || !dispatchData?.id) {
      setAuditData(null);
      return undefined;
    }

    const auditRef = doc(db, 'dispatch_audit', dispatchData.id);
    const unsubscribe = onSnapshot(auditRef, (snapshot) => {
      setAuditData(snapshot.exists() ? snapshot.data() : null);
    });

    return () => unsubscribe();
  }, [db, dispatchData?.id, isOpen]);

  const history = useMemo(() => {
    const items = Array.isArray(auditData?.history) ? auditData.history : [];
    return [...items].sort((a, b) => {
      const aTime = new Date(a.actionAt || a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.actionAt || b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [auditData]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        zIndex: 9998
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '440px',
          maxWidth: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-12px 0 28px rgba(15, 23, 42, 0.18)',
          padding: '24px 24px 32px 24px',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#111827' }}>Dispatch History</h3>
            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
              #{dispatchData?.mocNo || dispatchData?.id || auditData?.dispatchId || '---'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gap: '14px', marginBottom: '20px' }}>
          <div style={historySummaryCardStyle}>
            <div style={historyMetaLabelStyle}>Created</div>
            <div style={historyPrimaryValueStyle}>{formatHistoryDateTime(auditData?.createdAt)}</div>
            <div style={historySecondaryValueStyle}>
              {auditData?.createdBy || '---'} {auditData?.createdByEmail ? `(${auditData.createdByEmail})` : ''}
            </div>
          </div>
          <div style={historySummaryCardStyle}>
            <div style={historyMetaLabelStyle}>Last Updated</div>
            <div style={historyPrimaryValueStyle}>{formatHistoryDateTime(auditData?.updatedAt)}</div>
            <div style={historySecondaryValueStyle}>
              {auditData?.updatedBy || '---'} {auditData?.updatedByEmail ? `(${auditData.updatedByEmail})` : ''}
            </div>
          </div>
          <div style={historySummaryCardStyle}>
            <div style={historyMetaLabelStyle}>Current Status</div>
            <div style={historyPrimaryValueStyle}>{formatStatusLabel(auditData?.dispatchStatus || dispatchData?.dispatchStatus)}</div>
            <div style={historySecondaryValueStyle}>Audit entries: {history.length}</div>
          </div>
        </div>

        <div style={{ marginBottom: '12px', fontSize: '12px', fontWeight: '800', color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Activity Log
        </div>

        {history.length === 0 ? (
          <div style={emptyHistoryStyle}>No dispatch history recorded yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {history.map((entry, index) => (
              <div key={`${entry.actionAt || entry.updatedAt || entry.createdAt || 'dispatch-history'}-${index}`} style={historyCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                    {formatHistoryAction(entry.action)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {formatHistoryDateTime(entry.actionAt || entry.updatedAt || entry.createdAt)}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '5px', fontSize: '13px', color: '#334155' }}>
                  <div>
                    <span style={historyMetaLabelInlineStyle}>Status</span>
                    <span style={historyStatusPillStyle}>{formatStatusLabel(entry.dispatchStatus)}</span>
                  </div>
                  <div>By: {entry.actionBy || entry.updatedBy || '---'}</div>
                  <div>Email: {entry.actionByEmail || entry.updatedByEmail || '---'}</div>
                  {entry.remarks ? <div>Remarks: {entry.remarks}</div> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DispatchHistoryTimeline = ({ dispatchData = {}, title = 'Dispatch Log', showHeader = true }) => {
  const dispatchTimeline = useMemo(
    () => getDispatchTimelineState(dispatchData),
    [dispatchData]
  );

  return (
    <div style={{ marginBottom: showHeader ? '30px' : 0 }}>
      {showHeader ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h4 style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#4b5563',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.025em'
          }}>
            <Package size={14} color="#6b7280" />
            {title}
          </h4>
        </div>
      ) : null}
      <div className="timeline-scroll-container" style={timelineContainerStyle}>
        <div style={timelineLineStyle} />
        {dispatchTimeline.stages.map((stage) => {
            const stageState = dispatchTimeline.stageStates?.[stage.id] || {};
            const isCompleted = Boolean(stageState.completed);
            const isActive = stage.id === dispatchTimeline.currentStageId && !isCompleted;
            const isFuture = !isCompleted && !isActive;
            const isRejected = stage.tone === 'rejected';
            const timestamp = dispatchTimeline.timestamps[stage.id];

          return (
            <div key={stage.id} style={timelineItemStyle}>
              <div style={timelineNodeStyle(isCompleted, isActive, isFuture, isRejected)}>
                {isCompleted ? <CheckCircle size={18} color="white" /> : stage.icon}
                {isActive && <div className="pulse-ring" style={pulseRingStyle(isRejected)} />}
              </div>
              <div style={timelineContentStyle(isCompleted, isActive)}>
                <span style={timelineLabelStyle(isActive, isRejected)}>{stage.label}</span>
                {(isCompleted || (isActive && timestamp)) && (
                  <span style={timelineDateStyle}>
                    {stage.id === 'dispatched'
                      ? formatDispatchOutTimestamp(dispatchData.vehicleOutTime, dispatchData.dispatchDate)
                      : formatTimelineDateTime(timestamp)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HorizontalTimeline = ({ db, orderData, dispatchesData, dispatchData, title, historyData }) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isDispatchHistoryOpen, setIsDispatchHistoryOpen] = useState(false);
  const normalizedTitle = String(title || '').trim().toLowerCase();

  if (normalizedTitle.startsWith('shipment tracker')) return null;

  const primaryDispatch = dispatchData || dispatchesData?.[0] || null;
  const isInterbranchTransfer =
    dispatchData?.typeOfMOC === 'Interbranch Transfer' ||
    dispatchesData?.some(dispatch => dispatch.typeOfMOC === 'Interbranch Transfer') ||
    primaryDispatch?.typeOfMOC === 'Interbranch Transfer';
  const isOrderTimeline = Boolean(orderData?.id) && !dispatchData && !dispatchesData;

  if (!orderData && !dispatchData && (!dispatchesData || dispatchesData.length === 0)) return null;

  const canShowHistoryLink = false;

  if (isOrderTimeline) {
    const orderTimeline = getOrderTimelineState(orderData, historyData);

    return (
      <div style={{ marginBottom: '30px' }}>
        {title && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h4 style={{
              fontSize: '13px',
              fontWeight: '700',
              color: '#4b5563',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em'
            }}>
              <Clock size={14} color="#2563eb" />
              {title}
            </h4>
          </div>
        )}
        <div className="timeline-scroll-container" style={timelineContainerStyle}>
          <div style={timelineLineStyle} />
          {orderTimeline.stages.map((stage, index) => {
            const isCompleted = index < orderTimeline.currentStageIndex;
            const isActive = index === orderTimeline.currentStageIndex;
            const isFuture = index > orderTimeline.currentStageIndex;
            const isRejected = stage.tone === 'rejected' && (isCompleted || isActive);
            const timestamp = orderTimeline.timestamps[stage.id];

            return (
              <div key={stage.id} style={timelineItemStyle}>
                <div style={timelineNodeStyle(isCompleted, isActive, isFuture, isRejected)}>
                  {isCompleted ? <CheckCircle size={18} color="white" /> : stage.icon}
                  {isActive && <div className="pulse-ring" style={pulseRingStyle(isRejected)} />}
                </div>
                <div style={timelineContentStyle(isCompleted, isActive)}>
                  <span style={timelineLabelStyle(isActive, isRejected)}>{stage.label}</span>
                  {(isCompleted || (isActive && timestamp)) && (
                    <span style={timelineDateStyle}>{formatTimelineTimestamp(timestamp)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (dispatchData) {
    const dispatchSnapshotTitle = `Dispatch: ${dispatchData?.mocNo || dispatchData?.id || '---'}`;

    return (
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h4 style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#4b5563',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.025em'
          }}>
            <Package size={14} color="#6b7280" />
            {dispatchSnapshotTitle}
          </h4>
          {db && dispatchData?.id ? (
            <button
              type="button"
              onClick={() => setIsDispatchHistoryOpen(true)}
              style={viewHistoryButtonStyle}
            >
              <History size={14} />
              View dispatch history
            </button>
          ) : null}
        </div>
        <DispatchHistoryTimeline dispatchData={dispatchData} title={title || 'Dispatch Log'} showHeader={false} />
        <DispatchHistoryDrawer
          isOpen={isDispatchHistoryOpen}
          onClose={() => setIsDispatchHistoryOpen(false)}
          db={db}
          dispatchData={dispatchData}
        />
      </div>
    );
  }

  const timelineStages = isInterbranchTransfer ? INTERBRANCH_TIMELINE_STAGES : TIMELINE_STAGES;
  const defaultStageId = isInterbranchTransfer ? 'moc_created' : 'order_created';

  let currentOverallStageId = defaultStageId;
  
  if (dispatchData) {
    // Specific MOC Timeline
    currentOverallStageId = getTimelineStageFromStatus(dispatchData.packingStatus || dispatchData.dispatchStatus, isInterbranchTransfer);
  } else if (dispatchesData && dispatchesData.length > 0) {
    // Aggregate Timeline (Master)
    let furthestIndex = -1;
    for (const dispatch of dispatchesData) {
      const stageId = getTimelineStageFromStatus(dispatch.packingStatus || dispatch.dispatchStatus, dispatch.typeOfMOC === 'Interbranch Transfer');
      const stageIndex = timelineStages.findIndex(s => s.id === stageId);
      if (stageIndex > furthestIndex) {
        furthestIndex = stageIndex;
        currentOverallStageId = stageId;
      }
    }
  } else {
    // Base Order Timeline (No MOCs yet)
    currentOverallStageId = isInterbranchTransfer ? defaultStageId : getTimelineStageFromStatus(orderData.status, false);
  }

  let currentStageIndex = timelineStages.findIndex(s => s.id === currentOverallStageId);
  if (currentStageIndex === -1) currentStageIndex = 0;

  // If the status is truly 'delivered' (not just 'in_transit'), we mark the 'delivered' 
  // stage as completed (green) instead of active (blue pulsing).
  if (currentOverallStageId === 'delivered') {
    if (dispatchData && dispatchData.dispatchStatus === 'delivered') {
      currentStageIndex = timelineStages.length;
    } else if (dispatchesData && dispatchesData.length > 0) {
      if (dispatchesData.every(d => d.dispatchStatus === 'delivered')) {
        currentStageIndex = timelineStages.length;
      }
    } else if (orderData?.status === 'delivered') {
      currentStageIndex = timelineStages.length;
    }
  }

  const getTimestampForStage = (stageId) => {
    if (stageId === 'order_created') {
      return orderData?.createdAt || null;
    }

    // If we are looking at a specific MOC
    if (dispatchData) {
        let matchedTimestamp = null;
        switch (stageId) {
            case 'moc_created': matchedTimestamp = dispatchData.createdAt; break;
            case 'moc_approved_finance': matchedTimestamp = dispatchData.financeApprovedAt; break;
            case 'packing_scheduled': matchedTimestamp = dispatchData.packingScheduledAt; break;
            case 'packed_ready': matchedTimestamp = dispatchData.packedAt; break;
            case 'dc_created': matchedTimestamp = dispatchData.dcCreatedAt; break;
            case 'documents_ready': matchedTimestamp = dispatchData.documentsReadyAt; break;
            case 'qc_dispatched': matchedTimestamp = dispatchData.dispatchedAt; break;
            case 'delivered': matchedTimestamp = dispatchData.deliveredAt; break;
            default: break;
        }
        return matchedTimestamp ? (matchedTimestamp.toDate ? matchedTimestamp.toDate() : new Date(matchedTimestamp)) : null;
    }

    // Aggregate logic for Master Timeline
    if (!dispatchesData || dispatchesData.length === 0) return null;

    let earliestMs = null;
    for (const dispatch of dispatchesData) {
        let matchedTimestamp = null;
        switch (stageId) {
            case 'moc_created': matchedTimestamp = dispatch.createdAt; break;
            case 'moc_approved_finance': matchedTimestamp = dispatch.financeApprovedAt; break;
            case 'packing_scheduled': matchedTimestamp = dispatch.packingScheduledAt; break;
            case 'packed_ready': matchedTimestamp = dispatch.packedAt; break;
            case 'dc_created': matchedTimestamp = dispatch.dcCreatedAt; break;
            case 'documents_ready': matchedTimestamp = dispatch.documentsReadyAt; break;
            case 'qc_dispatched': matchedTimestamp = dispatch.dispatchedAt; break;
            case 'delivered': matchedTimestamp = dispatch.deliveredAt; break;
            default: break;
        }

        if (matchedTimestamp) {
            const currentMs = matchedTimestamp.toDate ? matchedTimestamp.toDate().getTime() : new Date(matchedTimestamp).getTime();
            if (!isNaN(currentMs)) {
                if (earliestMs === null || currentMs < earliestMs) {
                    earliestMs = currentMs;
                }
            }
        }
    }
    return earliestMs ? new Date(earliestMs) : null;
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      {title && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h4 style={{ 
            fontSize: '13px', 
            fontWeight: '700', 
            color: '#4b5563',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.025em'
          }}>
            {dispatchData ? <Package size={14} color="#6b7280" /> : <Clock size={14} color="#2563eb" />}
            {title}
          </h4>
          {canShowHistoryLink ? (
            <button
              type="button"
              onClick={() => setIsHistoryOpen(true)}
              style={viewHistoryButtonStyle}
            >
              View Order History
            </button>
          ) : null}
        </div>
      )}
      <div className="timeline-scroll-container" style={timelineContainerStyle}>
        <div style={timelineLineStyle} />
        {timelineStages.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isActive = index === currentStageIndex;
          const isFuture = index > currentStageIndex;
          const timestamp = getTimestampForStage(stage.id);

          return (
            <div key={stage.id} style={timelineItemStyle}>
              <div style={timelineNodeStyle(isCompleted, isActive, isFuture)}>
                {isCompleted ? <CheckCircle size={18} color="white" /> : stage.icon}
                {isActive && <div className="pulse-ring" style={pulseRingStyle()} />}
              </div>
              <div style={timelineContentStyle(isCompleted, isActive)}>
                <span style={timelineLabelStyle(isActive)}>{stage.label}</span>
                {(isCompleted || (isActive && timestamp)) && (
                  <span style={timelineDateStyle}>{formatTimelineTimestamp(timestamp)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {canShowHistoryLink ? (
        <OrderHistoryDrawer
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          orderData={orderData}
          historyData={historyData}
        />
      ) : null}
    </div>
  );
};

// ---------------------------
// Pickup (PC/MIC) Timeline
// ---------------------------
const PICKUP_STAGES = [
  { id: 'pickup_intimation', label: 'Pickup Intimation', icon: <Clock size={18} /> },
  { id: 'pc_ready', label: 'Pickup Challan Ready', icon: <ClipboardList size={18} /> },
  { id: 'pickup_complete', label: 'Pickup Complete', icon: <CheckCircle size={18} /> },
  { id: 'mic_complete', label: 'MIC Complete', icon: <ShieldCheck size={18} /> },
  { id: 'material_received', label: 'Material Received', icon: <Warehouse size={18} /> },
];

const getPickupStageFromData = (pc, mic, qc) => {
  if (!pc) return 'pickup_intimation';

  // Order of checks is reverse (Latest status wins)
  const micStatus = qc?.micStatus || mic?.micStatus || pc.micStatus || pc.status;
  
  if (micStatus === 'received') return 'material_received';
  if (micStatus === 'qc_passed') return 'mic_complete';
  if (micStatus === 'pending_qc' || micStatus === 'mic_ready') return 'pickup_complete';
  if (micStatus === 'mic_draft') return 'pickup_complete';
  if (pc.status === 'pc_ready' || pc.status === 'pc_completed') return 'pc_ready';

  return 'pickup_intimation';
};

const parseAnyTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value.toDate) return value.toDate();
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    // Support YYYY-MM-DD or ISO strings
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
    return null;
  }
  return null;
};

const formatTimestampCompact = (timestamp) => {
  const d = parseAnyTimestamp(timestamp);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const PickupTimeline = ({ pcData, pcsData, micData, micsByPcId, activity, title = 'Pickup Activity' }) => {
  // Support both old props (for backward compat) and new activity prop
  const pc = activity?.pc || pcData;
  const mic = activity?.mic || micData;
  const qc = activity?.qc;

  if (!pc && (!pcsData || pcsData.length === 0)) return null;

  let currentOverallStageId = 'pickup_intimation';
  
  if (pc) {
    // Single Lineage Timeline
    currentOverallStageId = getPickupStageFromData(pc); // Pass only the single document
  } else if (pcsData && pcsData.length > 0) {
    // Aggregate Master Pickup Timeline
    let furthestIndex = -1;
    for (const p of pcsData) {
      const m = micsByPcId ? micsByPcId[p.id] : null;
      const stageId = getPickupStageFromData(p); // Pass only the single document
      const stageIndex = PICKUP_STAGES.findIndex(s => s.id === stageId);
      if (stageIndex > furthestIndex) {
        furthestIndex = stageIndex;
        currentOverallStageId = stageId;
      }
    }
  }

  let currentStageIndex = PICKUP_STAGES.findIndex(s => s.id === currentOverallStageId);

  // Mark final stage as complete if truly received
  if (currentOverallStageId === 'material_received') {
    const finalStatus = qc?.micStatus || mic?.micStatus || pc?.status;
    if (finalStatus === 'received') currentStageIndex = PICKUP_STAGES.length;
  }

  const getTimestampForStage = (stageId) => {
    const getSingleLineageTimestamp = (p, m, q, sId) => {
        switch (sId) {
            case 'pickup_intimation':
              if (p.status === 'pc_draft') return null;
              return parseAnyTimestamp(p.intimatedAt) || parseAnyTimestamp(p.intimationDate) || parseAnyTimestamp(p.createdAt);
            case 'pc_ready':
              return parseAnyTimestamp(p.pcReadyAt) || parseAnyTimestamp(p.readyAt) || (['pc_ready', 'pc_completed', 'mic_draft', 'mic_ready'].includes(p.status) ? parseAnyTimestamp(p.updatedAt) : null);
            case 'pickup_complete':
              return parseAnyTimestamp(p.completedAt) || parseAnyTimestamp(m?.createdAt) || (['pc_completed', 'mic_draft', 'mic_ready'].includes(p.status) ? parseAnyTimestamp(p.updatedAt) : null);
            case 'mic_complete':
              return parseAnyTimestamp(q?.qcPassedAt) || parseAnyTimestamp(m?.qcPassedAt) || parseAnyTimestamp(m?.micCompletedAt) || parseAnyTimestamp(m?.updatedAt);
            case 'material_received':
              return parseAnyTimestamp(q?.receivedAt) || parseAnyTimestamp(m?.receivedAt) || parseAnyTimestamp(m?.receivedDate) || parseAnyTimestamp(p.receivedAt);
            default:
              return null;
        }
    };

    if (pc) {
        return getSingleLineageTimestamp(pc, mic, qc, stageId);
    }

    if (!pcsData || pcsData.length === 0) return null;

    let earliestMs = null;
    for (const p of pcsData) {
        const m = micsByPcId ? micsByPcId[p.id] : null;
        const ts = getSingleLineageTimestamp(p, m, null, stageId);
        if (ts) {
            const ms = ts.getTime();
            if (earliestMs === null || ms < earliestMs) {
                earliestMs = ms;
            }
        }
    }
    return earliestMs ? new Date(earliestMs) : null;
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <h4 style={{
        fontSize: '13px',
        fontWeight: '700',
        color: '#4b5563',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        textTransform: 'uppercase',
        letterSpacing: '0.025em'
      }}>
        <Truck size={14} color="#2563eb" />
        {title}
      </h4>
      <div className="timeline-scroll-container" style={timelineContainerStyle}>
        <div style={timelineLineStyle} />
        {PICKUP_STAGES.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isActive = index === currentStageIndex;
          const isFuture = index > currentStageIndex;
          const timestamp = getTimestampForStage(stage.id);

          return (
            <div key={stage.id} style={timelineItemStyle}>
              <div style={timelineNodeStyle(isCompleted, isActive, isFuture)}>
                {isCompleted ? <CheckCircle size={18} color="white" /> : stage.icon}
                {isActive && <div className="pulse-ring" style={pulseRingStyle()} />}
              </div>
              <div style={timelineContentStyle(isCompleted, isActive)}>
                <span style={timelineLabelStyle(isActive)}>{stage.label}</span>
                {(isCompleted || (isActive && timestamp)) && (
                  <span style={timelineDateStyle}>{formatTimestampCompact(timestamp)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- STYLES ---
const timelineContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '30px 20px',
  backgroundColor: 'white',
  borderRadius: '12px',
  overflowX: 'auto',
  minWidth: '100%',
  border: '1px solid #e5e7eb',
  gap: '5px',
  position: 'relative',
  boxSizing: 'border-box',
};

const timelineLineStyle = {
  position: 'absolute',
  top: '48px',
  left: '60px',
  right: '60px',
  height: '2px',
  backgroundColor: '#e5e7eb',
  zIndex: 0,
};

const timelineItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  flex: '1 1 0',
  minWidth: '110px',
  textAlign: 'center',
  zIndex: 1,
};

const timelineNodeStyle = (isCompleted, isActive, isFuture, isRejected = false) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: isRejected ? '#ef4444' : (isCompleted ? '#10b981' : (isActive ? '#2563eb' : '#f3f4f6')),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: isCompleted || isActive ? 'white' : '#9ca3af',
  marginBottom: '12px',
  position: 'relative',
  border: isActive ? '2px solid white' : (isFuture ? '2px solid #e5e7eb' : 'none'),
  boxShadow: isActive ? `0 0 0 2px ${isRejected ? '#ef4444' : '#2563eb'}` : 'none',
  transition: 'all 0.3s ease',
  opacity: isFuture ? 0.6 : 1,
  filter: isFuture ? 'grayscale(40%)' : 'none',
});

const pulseRingStyle = (isRejected = false) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  border: `4px solid ${isRejected ? '#ef4444' : '#2563eb'}`,
  animation: 'pulse 2s infinite',
  boxSizing: 'border-box',
  pointerEvents: 'none',
});

const timelineContentStyle = (isCompleted, isActive) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
});

const timelineLabelStyle = (isActive, isRejected = false) => ({
  fontSize: '11px',
  fontWeight: isActive || isRejected ? '700' : '600',
  color: isRejected ? '#b91c1c' : (isActive ? '#1e293b' : '#64748b'),
  lineHeight: '1.2',
  maxWidth: '90px',
});

const timelineDateStyle = {
  fontSize: '9px',
  color: '#94a3b8',
  fontWeight: '500',
};

const viewHistoryButtonStyle = {
  border: '1px solid #cbd5e1',
  backgroundColor: '#ffffff',
  color: '#1d4ed8',
  borderRadius: '999px',
  padding: '7px 12px',
  fontSize: '12px',
  fontWeight: '700',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
};

const historySummaryCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  padding: '14px 16px'
};

const historyMetaLabelStyle = {
  fontSize: '11px',
  fontWeight: '800',
  letterSpacing: '0.08em',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '6px'
};

const historyPrimaryValueStyle = {
  fontSize: '15px',
  fontWeight: '700',
  color: '#111827',
  marginBottom: '4px'
};

const historySecondaryValueStyle = {
  fontSize: '13px',
  color: '#334155'
};

const historyMetaLabelInlineStyle = {
  display: 'inline-block',
  marginRight: '8px',
  color: '#64748b',
  fontWeight: '700',
  fontSize: '12px'
};

const historyStatusPillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: '999px',
  backgroundColor: '#eff6ff',
  color: '#1d4ed8',
  fontSize: '12px',
  fontWeight: '700'
};

const historyCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  padding: '14px 16px'
};

const emptyHistoryStyle = {
  padding: '16px',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  backgroundColor: '#f8fafc',
  color: '#64748b',
  fontSize: '13px'
};

const keyframes = `
  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.6; }
    70% { transform: scale(1.5); opacity: 0; }
    100% { transform: scale(1); opacity: 0; }
  }
  .timeline-scroll-container::-webkit-scrollbar {
    display: none;
  }
  .timeline-scroll-container {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

if (typeof document !== 'undefined') {
  const styleId = 'timeline-animations';
  if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement('style');
    styleSheet.id = styleId;
    styleSheet.type = 'text/css';
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
  }
}

export default HorizontalTimeline;
